"use client";

import { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  Button,
  Box,
} from "@chakra-ui/react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import OTPInput from "./OTPInput";

export default function PasswordResetOTP() {
  const [error, setError] = useState<string | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || "";

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleVerify = async (otp: string) => {
    if (!email) {
      setError("Email information is missing");
      return;
    }

    setError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "recovery",
      });

      if (error) throw error;

      navigate("/set-new-password");
    } catch (error: any) {
      setError(error.error_description || error.message);
    } finally {
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      setError("Email information is missing");
      return;
    }

    setResendDisabled(true);
    setCountdown(60);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-new-password`,
      });

      if (error) throw error;
    } catch (error: any) {
      setError(error.error_description || error.message);
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  if (!email) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} textAlign="center">
              <Heading size="lg" color="red.500">
                Error
              </Heading>
              <Text color="gray.600">Email information is missing.</Text>
              <Button as={Link} to="/forgot-password" colorScheme="primary">
                Back to Password Reset
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box
                as="img"
                src="/main-logo.png"
                alt="CallSupplai Logo"
                h="60px"
                mx="auto"
                mb={2}
              />
              <Text color="secondary.600" fontSize="sm">
                We've sent a verification code to{" "}
                <Text as="span" color="blue.600">
                  {email}
                </Text>
                . Please enter the code below to continue.
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <OTPInput onComplete={handleVerify} />

            <VStack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Didn't receive the code?{" "}
                <Button
                  variant="link"
                  colorScheme="primary"
                  onClick={handleResendOTP}
                  isDisabled={resendDisabled}
                  fontSize="sm"
                  p={0}
                  h="auto"
                  minW="auto"
                >
                  {resendDisabled ? `Resend in ${countdown}s` : "Resend OTP"}
                </Button>
              </Text>

              <Button
                as={Link}
                to="/forgot-password"
                variant="outline"
                w="100%"
                colorScheme="primary"
              >
                Back to Password Reset
              </Button>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
